import type { ReactNode } from 'react';

import { Card, CardDescription, CardHeader, CardTitle, Skeleton, cn } from '@giyus/ui';

export interface ChartCardProps {
  readonly title: ReactNode;
  readonly description?: ReactNode;
  /** Gráfico/conteúdo. Ignorado enquanto `loading` ou quando há `emptyState`. */
  readonly children?: ReactNode;
  readonly loading?: boolean;
  /** Rótulo do esqueleto para leitores de tela (ex.: "Carregando"). */
  readonly loadingLabel?: string;
  /** Estado vazio honesto (`EmptyState`): quando presente substitui o conteúdo (REGRA 01: nunca preencher com exemplos). */
  readonly emptyState?: ReactNode;
  /** Ação no cabeçalho (ex.: link "Ver todos"). */
  readonly action?: ReactNode;
  readonly headingLevel?: 'h2' | 'h3' | 'h4';
  readonly className?: string;
}

/** Card de painel: título + descrição + (carregando | vazio | conteúdo), com os estados desenhados (DS-08). */
export function ChartCard({
  title,
  description,
  children,
  loading = false,
  loadingLabel,
  emptyState,
  action,
  headingLevel = 'h2',
  className,
}: ChartCardProps) {
  return (
    <Card data-slot="chart-card" className={cn('min-w-0', className)}>
      <CardHeader className="flex-row flex-wrap items-start justify-between gap-x-4 gap-y-1">
        <div className="flex min-w-0 flex-col gap-1">
          <CardTitle as={headingLevel}>{title}</CardTitle>
          {description ? <CardDescription>{description}</CardDescription> : null}
        </div>
        {action ? <div className="shrink-0 text-body-sm">{action}</div> : null}
      </CardHeader>
      {loading ? (
        <div role="status" aria-busy="true" className="flex flex-col gap-2">
          {loadingLabel ? <span className="sr-only">{loadingLabel}</span> : null}
          <Skeleton className="h-4 w-3/4" />
          <Skeleton className="h-4 w-1/2" />
          <Skeleton className="h-4 w-2/3" />
        </div>
      ) : (
        (emptyState ?? children)
      )}
    </Card>
  );
}
